// PatternFlow — Shopify section wizard logic
// Source: JulietaShopify/js/patternflow.js + patternflow-data.js, refactored to
// read all copy, blocks, and mapping data from a per-section JSON config blob.
// One PatternFlow instance is created per script[data-pf-config] node.

(function () {
  'use strict';

  // ========== Helpers ==========
  function clear(el) { if (el) el.innerHTML = ''; }

  function uniq(arr) {
    var seen = {};
    var out = [];
    for (var i = 0; i < arr.length; i++) {
      var v = arr[i];
      if (v == null) continue;
      if (!seen[v]) { seen[v] = 1; out.push(v); }
    }
    return out;
  }

  // Normalize lookup keys: trim whitespace, strip apostrophes and curly quotes.
  // Lets `Women's` match `Womens`, `Pull-on ` match `Pull-On`, etc.
  function normKey(s) {
    if (s == null) return null;
    return String(s).replace(/[‘’']/g, '').trim();
  }

  // Alias maps so the spreadsheet's vocabulary (e.g. "Set In", "Sport/Swim Crop")
  // resolves to the UI's vocabulary ("Set-In Armhole", "Crop Top").
  var STEP4_ALIAS = {
    'Set In':  'Set-In Armhole',
    'Raglan':  'Raglan Armhole',
    'Pull-on': 'Pull-On',
    'Zip Up':  'Zip'
  };
  var PRODUCT_TYPE_ALIAS = {
    'Sport/Swim Crop': 'Crop Top',
    'Blouse/Top':      'Blouse / Top'
  };

  function aliasStep4(s) {
    var n = normKey(s);
    if (n == null) return null;
    return STEP4_ALIAS[n] || n;
  }
  function aliasProductType(s) {
    var n = normKey(s);
    if (n == null) return null;
    return PRODUCT_TYPE_ALIAS[n] || n;
  }

  // Match a row's sport against the active path. sport === null means the
  // normal (non-sportswear) path, which only matches rows with no sport.
  function sportMatches(rowSport, wantSport) {
    var rs = normKey(rowSport);
    if (wantSport == null) return rs == null;
    return rs === normKey(wantSport);
  }

  // Identity key for one full wizard path, used by the builder-URL overrides.
  // `sport` is part of the key because several garments (Tee, Singlet, Dress,
  // Skirt, Leggings, Crop Top) exist in both the base and the sport mapping.
  // Values are normalized/aliased exactly like the mapping, so merchant
  // vocabulary ("Set In", "Pull-on") lands on the same key the rows do.
  function pathKey(cat, sport, pt, s4, s5) {
    var c = normKey(cat), p = aliasProductType(pt);
    if (!c || !p) return null;
    return [c, normKey(sport) || '-', p, aliasStep4(s4) || '-', normKey(s5) || '-'].join('|');
  }

  // Derive category→productTypes, productType→step4, and the sportswear
  // lookups (category→sports, category→sport→productTypes) from the mapping.
  // Rows carrying a `sport` feed the sportswear path and are kept out of the
  // normal category product-type lists.
  function deriveLookups(mapping) {
    var catTypes = {};
    var step4ByType = {};
    var catSports = {};      // catSports[category] = [sport, ...]
    var sportTypes = {};     // sportTypes[category][sport] = [productType, ...]

    mapping.forEach(function (row) {
      var cat = normKey(row.category);
      var pt  = aliasProductType(row.productType);
      var s4  = aliasStep4(row.step4);
      var sport = normKey(row.sport);
      if (!cat || !pt) return;

      if (sport) {
        if (!catSports[cat]) catSports[cat] = [];
        if (catSports[cat].indexOf(sport) === -1) catSports[cat].push(sport);
        if (!sportTypes[cat]) sportTypes[cat] = {};
        if (!sportTypes[cat][sport]) sportTypes[cat][sport] = [];
        if (sportTypes[cat][sport].indexOf(pt) === -1) sportTypes[cat][sport].push(pt);
      } else {
        if (!catTypes[cat]) catTypes[cat] = [];
        if (catTypes[cat].indexOf(pt) === -1) catTypes[cat].push(pt);
      }

      if (!step4ByType[pt]) step4ByType[pt] = [];
      if (s4 != null && step4ByType[pt].indexOf(s4) === -1) step4ByType[pt].push(s4);
    });

    // Convert step4 arrays: empty array => null (skip step 4)
    Object.keys(step4ByType).forEach(function (k) {
      if (step4ByType[k].length === 0) step4ByType[k] = null;
    });

    return {
      categoryProductTypes: catTypes,
      step4Options: step4ByType,
      categorySports: catSports,
      sportProductTypes: sportTypes
    };
  }

  // Path-aware step4 options for a specific category + sport + productType.
  // Returns null when that path has no step4 dimension (skip step 4).
  function getStep4OptionsForPath(category, sport, productType, mapping) {
    var nCat = normKey(category);
    var nPt  = aliasProductType(productType);
    var arr = [];
    for (var i = 0; i < mapping.length; i++) {
      var r = mapping[i];
      if (normKey(r.category) !== nCat) continue;
      if (aliasProductType(r.productType) !== nPt) continue;
      if (!sportMatches(r.sport, sport)) continue;
      var s4 = aliasStep4(r.step4);
      if (s4 != null && arr.indexOf(s4) === -1) arr.push(s4);
    }
    return arr.length === 0 ? null : arr;
  }

  function getStep5OptionsForPath(category, sport, productType, step4, mapping) {
    var nCat = normKey(category);
    var nPt = aliasProductType(productType);
    var nS4 = aliasStep4(step4);
    var step5s = [];
    for (var i = 0; i < mapping.length; i++) {
      var r = mapping[i];
      if (normKey(r.category) !== nCat) continue;
      if (aliasProductType(r.productType) !== nPt) continue;
      if (aliasStep4(r.step4) !== nS4) continue;
      if (!sportMatches(r.sport, sport)) continue;
      var s5 = normKey(r.step5);
      if (s5 != null && step5s.indexOf(s5) === -1) step5s.push(s5);
    }
    return step5s.length === 0 ? null : step5s;
  }

  function resolveProducts(category, sport, productType, step4, step5, mapping) {
    var nCat = normKey(category);
    var nPt  = aliasProductType(productType);
    var nS4  = aliasStep4(step4);
    var nS5  = normKey(step5);
    return mapping.filter(function (p) {
      if (normKey(p.category) !== nCat) return false;
      if (aliasProductType(p.productType) !== nPt) return false;
      if (!sportMatches(p.sport, sport)) return false;
      var rS4 = aliasStep4(p.step4);
      var rS5 = normKey(p.step5);
      if (nS4 === null && rS4 !== null) return false;
      if (nS4 !== null && rS4 !== nS4) return false;
      if (nS5 === null && rS5 !== null) return false;
      if (nS5 !== null && rS5 !== nS5) return false;
      return true;
    });
  }

  // ========== Wizard instance ==========
  function PatternFlow(rootEl, config) {
    var ICONS = window.PATTERNFLOW_ICONS || {};
    var ICON_MAPS = window.PATTERNFLOW_ICON_MAPS || { productType: {}, step4: {}, category: {}, productTypeByCategory: {}, sport: {} };

    var copy = config.copy || {};
    // Base mapping: section's mapping_json override if present, otherwise the
    // bundled spreadsheet export at assets/patternflow-mapping.js.
    var baseMapping = (Array.isArray(config.mapping) && config.mapping.length)
      ? config.mapping
      : (Array.isArray(window.PATTERNFLOW_MAPPING) ? window.PATTERNFLOW_MAPPING : []);
    // Sportswear rows are kept in a separate, additive list so they never have
    // to be merged into (or risk overwriting) the live base mapping file.
    var sportMapping = Array.isArray(config.sportMapping) ? config.sportMapping : [];
    var mapping = baseMapping.concat(sportMapping);
    var lookups = deriveLookups(mapping);
    var panelImages = config.panelImages || {};

    // Per-path builder URL overrides (product_urls_json). Lets a merchant
    // re-point the "Continue to Builder" link for any single combination from
    // the theme editor without editing assets/patternflow-mapping.js.
    // A per-combination *block* is impossible here: 261 combinations vs
    // Shopify's ~50-block cap, hence the JSON setting.
    //
    // Two accepted shapes — both produce the same lookup:
    //   compact (preferred, ~half the size, what the generated template uses)
    //     { "Mens|-|Tee|Raglan Armhole|-": "https://…" }
    //     key = category|sport|productType|step4|step5, "-" for any absent part
    //   verbose (original, self-documenting)
    //     [ { "category","sport","productType","step4","step5","url" } ]
    // Blank/absent urls are skipped, so a template row left empty is inert and
    // simply falls through to the mapping's productUrl.
    //
    // Two sources feed this, merged in order:
    //   1. product_urls_json — the bulk JSON box on this section.
    //   2. The individual "Builder link" fields. Shopify caps a section at 250
    //      settings and 261 combinations don't fit, so those fields live in
    //      separate companion sections (PF links — Men's, Women's, Kids,
    //      Babies, Sport). Each renders a [data-pf-urls] JSON blob; we collect
    //      every one on the page. Fields win where filled — a blank field is
    //      skipped, so it never wipes a value set in the JSON box.
    function parseUrlOverrides(src) {
      var out = {};
      if (Array.isArray(src)) {
        src.forEach(function (o) {
          if (!o || !o.url) return;
          var k = pathKey(o.category, o.sport, o.productType, o.step4, o.step5);
          if (k) out[k] = o.url;
        });
      } else if (src && typeof src === 'object') {
        // A key that isn't 5 parts is ignored, so a "_README" note can safely
        // live inside the pasted template.
        Object.keys(src).forEach(function (raw) {
          var url = src[raw];
          if (!url || typeof url !== 'string') return;
          var p = String(raw).split('|');
          if (p.length !== 5) return;
          var part = function (v) { v = (v == null ? '' : String(v)).trim(); return (v === '' || v === '-') ? null : v; };
          var k = pathKey(part(p[0]), part(p[1]), part(p[2]), part(p[3]), part(p[4]));
          if (k) out[k] = url;
        });
      }
      return out;
    }
    var productUrlOverrides = parseUrlOverrides(config.productUrls);
    [].forEach.call(document.querySelectorAll('script[data-pf-urls]'), function (node) {
      var parsed;
      try { parsed = JSON.parse(node.textContent); }
      catch (e) { return; } // one malformed links section must not break the wizard
      var one = parseUrlOverrides(parsed);
      Object.keys(one).forEach(function (k) { productUrlOverrides[k] = one[k]; });
    });

    // A row's effective builder URL: override → row productUrl → '' (no builder
    // yet, so the caller routes to the Fit Finder instead).
    function resolveRowUrl(row) {
      if (!row) return '';
      var ov = productUrlOverrides[pathKey(row.category, row.sport, row.productType, row.step4, row.step5)];
      if (ov && ov !== '#' && String(ov).trim() !== '') return ov;
      var u = row.productUrl;
      return (u && u !== '#' && String(u).trim() !== '') ? u : '';
    }

    // Build lookup tables for block-provided card metadata (label + image overrides)
    var categoryMeta = {};
    (config.categories || []).forEach(function (b) { if (b.key) categoryMeta[b.key] = b; });

    var productTypeMeta = {};
    (config.productTypes || []).forEach(function (b) { if (b.key) productTypeMeta[b.key] = b; });

    var step4Meta = {};
    (config.step4Options || []).forEach(function (b) { if (b.key) step4Meta[b.key] = b; });

    var step5Meta = {};
    (config.step5Options || []).forEach(function (b) { if (b.key) step5Meta[b.key] = b; });

    var sportMeta = {};
    (config.sportOptions || []).forEach(function (b) { if (b.key) sportMeta[b.key] = b; });

    // Per-(category, productType, step4-option) refinement images (Requirement 2).
    // Nested lookup: refinementImages[category][productType][option] = url.
    // Keys are normalized/aliased exactly like the mapping so merchant vocabulary
    // ("Set In", "Pull-on") resolves to the UI's ("Set-In Armhole", "Pull-On").
    // The JSON override (refinement_images_json) is processed first, then the
    // refinement_image blocks, so a block wins over the JSON on the same
    // (category, productType, option) — blocks are the friendlier, authoritative
    // source; the JSON is the escape hatch for scaling past Shopify's block cap.
    var refinementImages = {};
    var refinementImageEntries = (Array.isArray(config.refinementImagesJson) ? config.refinementImagesJson : [])
      .concat(config.refinementImages || []);
    refinementImageEntries.forEach(function (b) {
      if (!b || !b.image) return;
      var c = normKey(b.category), p = aliasProductType(b.productType), k = aliasStep4(b.key);
      if (!c || !p || !k) return;
      if (!refinementImages[c]) refinementImages[c] = {};
      if (!refinementImages[c][p]) refinementImages[c][p] = {};
      refinementImages[c][p][k] = b.image;
    });

    // Per-style image cards (sections/patternflow-step4-images.liquid). Each card
    // carries up to 4 category uploads; the section explodes them into
    //   { category, productType, step4, image }
    // entries and emits one [data-pf-step4-images] blob. We collect every such
    // blob on the page — mirroring parseUrlOverrides' sweep of [data-pf-urls] —
    // because these cards live in their own section (the wizard is near Shopify's
    // 50-block / 250-setting caps). A card with a real sleeve feeds the Step-4
    // visual (refinementImages, the same lookup the refinement_image blocks use);
    // a card whose sleeve is "-" (single-style garments: Singlet, Leggings,
    // Brief) feeds the garment-card visual (productTypeImages) instead. Processed
    // last, so a per-style card wins over a refinement_image block on the same
    // (category, productType, step4). A blank upload emits nothing, so it is inert.
    var productTypeImages = {}; // productTypeImages[category][productType] = url
    [].forEach.call(document.querySelectorAll('script[data-pf-step4-images]'), function (node) {
      var parsed;
      try { parsed = JSON.parse(node.textContent); }
      catch (e) { return; } // one malformed section must not break the wizard
      (Array.isArray(parsed) ? parsed : []).forEach(function (b) {
        if (!b || !b.image) return;
        var c = normKey(b.category), p = aliasProductType(b.productType);
        if (!c || !p) return;
        var raw = normKey(b.step4);
        if (raw === '-' || raw === '') raw = null;
        var k = aliasStep4(raw);
        if (k) {
          if (!refinementImages[c]) refinementImages[c] = {};
          if (!refinementImages[c][p]) refinementImages[c][p] = {};
          refinementImages[c][p][k] = b.image;
        } else {
          if (!productTypeImages[c]) productTypeImages[c] = {};
          productTypeImages[c][p] = b.image;
        }
      });
    });

    // Per-(category, sport, garment) card images (sections/patternflow-sport-images.liquid).
    // Each card carries up to 3 category uploads; the section explodes them into
    //   { category, sport, productType, image }
    // entries and emits one [data-pf-sport-images] blob. We collect every such
    // blob on the page — mirroring the [data-pf-urls] and [data-pf-step4-images]
    // sweeps above — because these cards live in their own section (the wizard is
    // near Shopify's 50-block / 250-setting caps).
    //
    // The grain is (sport, garment), not garment alone: Jersey appears under Ice
    // Hockey, Baseball and Cycling, Short under four sports, and six sport
    // garments (Tee, Singlet, Dress, Skirt, Leggings, Crop Top) share a key with a
    // normal garment — so a Soccer Tee has to be able to differ from the base Tee.
    // There is no Babies entry: Babies has no sport rows, so the sportswear branch
    // never renders for it. A blank upload emits nothing, so a card is inert.
    var sportProductTypeImages = {}; // sportProductTypeImages[category][sport][productType] = url
    [].forEach.call(document.querySelectorAll('script[data-pf-sport-images]'), function (node) {
      var parsed;
      try { parsed = JSON.parse(node.textContent); }
      catch (e) { return; } // one malformed section must not break the wizard
      (Array.isArray(parsed) ? parsed : []).forEach(function (b) {
        if (!b || !b.image) return;
        var c = normKey(b.category), s = normKey(b.sport), p = aliasProductType(b.productType);
        if (!c || !s || !p) return;
        if (!sportProductTypeImages[c]) sportProductTypeImages[c] = {};
        if (!sportProductTypeImages[c][s]) sportProductTypeImages[c][s] = {};
        sportProductTypeImages[c][s][p] = b.image;
      });
    });

    // Determine the ordered list of categories to render — block order wins; fall back to mapping order
    var orderedCategories = (config.categories && config.categories.length)
      ? config.categories.map(function (b) { return b.key; })
      : uniq(mapping.map(function (r) { return r.category; }));

    // State
    var state = {
      selectedCategory: null,
      selectedStartMethod: null,
      selectedSport: null,
      selectedProductType: null,
      selectedStep4: null,
      selectedStep5: null,
      fitFinderRecommended: false
    };

    // Scoped DOM lookups
    var $ = function (sel) { return rootEl.querySelector(sel); };
    var $$ = function (sel) { return rootEl.querySelectorAll(sel); };

    var $breadcrumb = $('[data-pf="breadcrumb"]');
    var $progressFill = $('[data-pf="progress-fill"]');

    var steps = {
      category:  $('[data-step="category"]'),
      start:     $('[data-step="start"]'),
      fitfinder: $('[data-step="fitfinder"]'),
      sport:     $('[data-step="sport"]'),
      product:   $('[data-step="product"]'),
      step4:     $('[data-step="step4"]'),
      step5:     $('[data-step="step5"]'),
      result:    $('[data-step="result"]')
    };

    function showStep(key) {
      var el = steps[key];
      if (!el) return;
      el.classList.add('pf-step--active');
      setTimeout(function () {
        scrollToStep(el);
      }, 80);
    }

    function scrollToStep(el) {
      var progressBar = rootEl.querySelector('.pf-progress');
      var progressHeight = progressBar ? progressBar.offsetHeight : 0;
      var stickyTop = 0;
      if (progressBar) {
        var topVal = parseInt(window.getComputedStyle(progressBar).top, 10);
        if (!isNaN(topVal)) stickyTop = topVal;
      }
      var offset = progressHeight + stickyTop + 16;
      var target = el.getBoundingClientRect().top + window.pageYOffset - offset;
      if (target < 0) target = 0;
      window.scrollTo({ top: target, behavior: 'smooth' });
    }

    function hideStep(key) {
      var el = steps[key];
      if (el) el.classList.remove('pf-step--active');
    }

    function hideSteps(keys) { keys.forEach(hideStep); }

    function updateProgress() {
      var total = 5;
      var done = 0;
      if (state.selectedCategory) done++;
      if (state.selectedStartMethod || state.selectedSport) done++;
      if (state.selectedProductType) done++;
      if (state.selectedStep4) done++;
      if (state.selectedStep5) done++;
      if ($progressFill) $progressFill.style.width = Math.round((done / total) * 100) + '%';
    }

    // ========== Visual helpers ==========
    function categoryLabel(cat) {
      var meta = categoryMeta[cat];
      if (meta && meta.label) return meta.label;
      var fallback = { Mens: "Men's", Womens: "Women's", Kids: 'Kids', Babies: 'Babies' };
      return fallback[cat] || cat;
    }

    function productTypeLabel(pt) {
      var meta = productTypeMeta[pt];
      return (meta && meta.label) ? meta.label : pt;
    }

    // Sleeve/closure card text, category-aware: the same option can read
    // differently per category (e.g. Babies "Popper Opening" vs Men's "Zip").
    // Precedence: per-category label → general label → the raw option key.
    function step4Label(opt, category) {
      var meta = step4Meta[opt];
      if (!meta) return opt;
      var cat = category ? String(category).toLowerCase() : '';
      if (cat && meta['label_' + cat]) return meta['label_' + cat];
      return meta.label || opt;
    }

    function step5Label(opt) {
      var meta = step5Meta[opt];
      return (meta && meta.label) ? meta.label : opt;
    }

    function sportLabel(sp) {
      var meta = sportMeta[sp];
      return (meta && meta.label) ? meta.label : sp;
    }

    function cardVisual(image, fallbackIconKey) {
      if (image) {
        return '<img src="' + image + '" alt="" loading="lazy">';
      }
      return ICONS[fallbackIconKey] || ICONS.tee || '';
    }

    // Resolve a product-type card visual with a category-aware precedence so
    // the same garment shows a different silhouette per category (Requirement 1):
    //   1. category-specific block image  (meta.image_<cat>)
    //   2. category-specific built-in icon (ICON_MAPS.productTypeByCategory)
    //   3. generic block image            (meta.image)
    //   4. generic built-in icon          (ICON_MAPS.productType)
    function resolveProductTypeVisual(pt, category, meta) {
      meta = meta || {};
      var catKeyLower = category ? String(category).toLowerCase() : null;

      var c = normKey(category), p = aliasProductType(pt);

      // Sportswear: a per-(category, sport, garment) upload from the "PF sport
      // images" section is the most specific source there is, so it wins — a
      // Soccer Tee must be able to differ from the base Tee, and an Ice Hockey
      // Jersey from a Cycling Jersey. Only reachable inside the sportswear
      // branch; in the normal flow state.selectedSport is null and this whole
      // check is skipped, leaving the existing precedence untouched.
      var activeSport = normKey(state.selectedSport);
      if (activeSport && c && p &&
          sportProductTypeImages[c] &&
          sportProductTypeImages[c][activeSport] &&
          sportProductTypeImages[c][activeSport][p]) {
        return '<img src="' + sportProductTypeImages[c][activeSport][p] + '" alt="" loading="lazy">';
      }

      // Per-style card upload (patternflow-step4-images) for single-style garments
      // (Singlet, Leggings, Brief) wins — the friendly, authoritative source.
      if (c && p && productTypeImages[c] && productTypeImages[c][p]) {
        return '<img src="' + productTypeImages[c][p] + '" alt="" loading="lazy">';
      }

      if (catKeyLower) {
        var catImg = meta['image_' + catKeyLower];
        if (catImg) return '<img src="' + catImg + '" alt="" loading="lazy">';
      }
      if (category) {
        var byCat = ICON_MAPS.productTypeByCategory && ICON_MAPS.productTypeByCategory[category];
        var catIconKey = byCat && byCat[pt];
        if (catIconKey && ICONS[catIconKey]) return ICONS[catIconKey];
      }
      if (meta.image) return '<img src="' + meta.image + '" alt="" loading="lazy">';
      var key = (ICON_MAPS.productType && ICON_MAPS.productType[pt]) || 'tee';
      return ICONS[key] || ICONS.tee || '';
    }

    // Category + garment aware step4 card visual. Precedence:
    //   1. per-(category, productType, option) merchant image (refinementImages) —
    //      finest grain, so Men's Tee Raglan can differ from Men's Shirt Raglan
    //   2. per-(category, option) block image (meta.image_<cat>) — the common case:
    //      one artwork per category on the Sleeve / Closure card itself
    //   3. category-specific built-in step4 icon (future hook — ICON_MAPS.step4ByCategory)
    //   4. global step4_option block image (meta.image)
    //   5. generic built-in step4 icon
    function resolveStep4Visual(opt, category, productType, meta) {
      meta = meta || {};
      var c = normKey(category), p = aliasProductType(productType), k = aliasStep4(opt);
      if (c && p && k && refinementImages[c] && refinementImages[c][p] && refinementImages[c][p][k]) {
        return '<img src="' + refinementImages[c][p][k] + '" alt="" loading="lazy">';
      }
      if (category) {
        var catImg = meta['image_' + String(category).toLowerCase()];
        if (catImg) return '<img src="' + catImg + '" alt="" loading="lazy">';
      }
      if (category) {
        var byCat = ICON_MAPS.step4ByCategory && ICON_MAPS.step4ByCategory[category];
        var catIconKey = byCat && byCat[opt];
        if (catIconKey && ICONS[catIconKey]) return ICONS[catIconKey];
      }
      if (meta.image) return '<img src="' + meta.image + '" alt="" loading="lazy">';
      var key = (ICON_MAPS.step4 && ICON_MAPS.step4[opt]) || 'setin';
      return ICONS[key] || ICONS.setin || '';
    }

    // Category-aware sport card visual, mirroring resolveProductTypeVisual so the
    // same sport can show a different image per category (client feedback).
    // Precedence:
    //   1. per-category block image   (meta.image_<cat>)
    //   2. per-category built-in icon (future hook — ICON_MAPS.sportByCategory)
    //   3. generic block image        (meta.image)
    //   4. built-in sport icon
    // Steps 3 and 4 are exactly what cardVisual() did here before, so a sport with
    // no per-category upload renders identically to the previous build. There is no
    // Babies branch because Babies has no sports.
    function resolveSportVisual(sp, category, meta, fallbackIconKey) {
      meta = meta || {};
      if (category) {
        var catImg = meta['image_' + String(category).toLowerCase()];
        if (catImg) return '<img src="' + catImg + '" alt="" loading="lazy">';
      }
      if (category) {
        var byCat = ICON_MAPS.sportByCategory && ICON_MAPS.sportByCategory[category];
        var catIconKey = byCat && byCat[sp];
        if (catIconKey && ICONS[catIconKey]) return ICONS[catIconKey];
      }
      if (meta.image) return '<img src="' + meta.image + '" alt="" loading="lazy">';
      return ICONS[fallbackIconKey] || ICONS.tee || '';
    }

    // ========== Breadcrumb ==========
    function updateBreadcrumb() {
      clear($breadcrumb);
      var crumbs = [];

      if (state.selectedCategory) {
        crumbs.push({ label: categoryLabel(state.selectedCategory), action: function () { selectCategory(null); } });
      }
      if (state.selectedSport) {
        crumbs.push({ label: sportLabel(state.selectedSport), action: function () { reopenSports(); } });
      }
      if (state.selectedProductType) {
        crumbs.push({ label: productTypeLabel(state.selectedProductType), action: function () { selectProductType(null); } });
      }
      if (state.selectedStep4) {
        crumbs.push({ label: step4Label(state.selectedStep4, state.selectedCategory), action: function () { selectStep4(null); } });
      }
      if (state.selectedStep5) {
        crumbs.push({ label: step5Label(state.selectedStep5), action: null });
      }

      crumbs.forEach(function (c, i) {
        if (i > 0) {
          var sep = document.createElement('span');
          sep.className = 'pf-crumb__sep';
          sep.textContent = '›';
          $breadcrumb.appendChild(sep);
        }
        var wrap = document.createElement('span');
        wrap.className = 'pf-crumb';

        if (c.action && i < crumbs.length - 1) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'pf-crumb__link';
          btn.textContent = c.label;
          btn.addEventListener('click', c.action);
          wrap.appendChild(btn);
        } else {
          var span = document.createElement('span');
          span.className = 'pf-crumb__current';
          span.textContent = c.label;
          wrap.appendChild(span);
        }
        $breadcrumb.appendChild(wrap);
      });

      updateProgress();
    }

    // ========== Card builders ==========
    function createCard(label, visualHtml, selected, onClick) {
      var card = document.createElement('div');
      card.className = 'pf-card' + (selected ? ' pf-card--selected' : '');
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      card.setAttribute('aria-label', label);
      card.setAttribute('aria-pressed', selected ? 'true' : 'false');

      var imgDiv = document.createElement('div');
      imgDiv.className = 'pf-card__img';
      imgDiv.innerHTML = visualHtml || '';

      var body = document.createElement('div');
      body.className = 'pf-card__body';
      var nameEl = document.createElement('span');
      nameEl.className = 'pf-card__name';
      nameEl.textContent = label;
      body.appendChild(nameEl);

      card.appendChild(imgDiv);
      card.appendChild(body);

      card.addEventListener('click', onClick);
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      });
      return card;
    }

    function createWideCard(iconSvg, title, desc, selected, onClick) {
      var card = document.createElement('div');
      card.className = 'pf-card pf-card--wide' + (selected ? ' pf-card--selected' : '');
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      card.setAttribute('aria-label', title);
      card.setAttribute('aria-pressed', selected ? 'true' : 'false');

      var iconWrap = document.createElement('div');
      iconWrap.className = 'pf-card__icon-wrap';
      iconWrap.innerHTML = iconSvg;

      var content = document.createElement('div');
      var nameEl = document.createElement('span');
      nameEl.className = 'pf-card__name';
      nameEl.textContent = title;
      var descEl = document.createElement('span');
      descEl.className = 'pf-card__desc';
      descEl.textContent = desc;
      content.appendChild(nameEl);
      content.appendChild(document.createElement('br'));
      content.appendChild(descEl);

      card.appendChild(iconWrap);
      card.appendChild(content);

      card.addEventListener('click', onClick);
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      });
      return card;
    }

    function createPill(label, selected, onClick) {
      var pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'pf-pill' + (selected ? ' pf-pill--selected' : '');
      pill.textContent = label;
      pill.setAttribute('aria-pressed', selected ? 'true' : 'false');
      pill.addEventListener('click', onClick);
      return pill;
    }

    // Reusable Fit Finder call-out (Requirement 4). Shown beside the result and
    // beside the Step 4 / Step 5 refinement screens. Links to the Fit Finder URL.
    function buildFitFinderCallout() {
      var box = document.createElement('div');
      box.className = 'pf-callout';
      var ff = copy.fitfinder_url;
      var href = (ff && ff !== '#' && String(ff).trim() !== '') ? ff : '#';
      var linkAttrs = href === '#' ? '' : ' target="_blank" rel="noopener"';
      box.innerHTML =
        '<div class="pf-callout__icon">' + (ICONS.search || '') + '</div>' +
        '<h4 class="pf-callout__title">' + escapeHtml(copy.callout_title || 'Trying to match a garment?') + '</h4>' +
        '<p class="pf-callout__text">' + escapeHtml(copy.callout_text || 'Click this link to use the Fit Finder and match your own garment.') + '</p>' +
        '<a class="pf-btn pf-btn--secondary pf-btn--sm" href="' + href + '"' + linkAttrs + '>' +
          (ICONS.external || '') + escapeHtml(copy.callout_btn || 'Use Fit Finder') +
        '</a>';
      return box;
    }

    // Mount the call-out into a step's callout slot (Step 4 / Step 5).
    function renderCallout(selector) {
      var host = $(selector);
      if (!host) return;
      clear(host);
      host.appendChild(buildFitFinderCallout());
    }

    // ========== Step 1: Category ==========
    function renderCategories() {
      var grid = $('[data-grid="category"]');
      clear(grid);
      orderedCategories.forEach(function (cat) {
        var meta = categoryMeta[cat] || {};
        var iconKey = ICON_MAPS.category[cat] || cat.toLowerCase();
        grid.appendChild(createCard(
          categoryLabel(cat),
          cardVisual(meta.image, iconKey),
          state.selectedCategory === cat,
          function () { selectCategory(cat); }
        ));
      });
    }

    function selectCategory(cat) {
      state.selectedCategory = cat;
      state.selectedStartMethod = null;
      state.selectedSport = null;
      state.selectedProductType = null;
      state.selectedStep4 = null;
      state.selectedStep5 = null;
      state.fitFinderRecommended = false;

      hideSteps(['start', 'fitfinder', 'sport', 'product', 'step4', 'step5', 'result']);
      renderCategories();

      if (cat) {
        renderStartMethod();
        showStep('start');
      }
      updateBreadcrumb();
    }

    // ========== Step 2: Start method ==========
    // Left card = browse this category's base styles. Right card = sportswear,
    // which re-asks gender (Men/Women/Kids) before drilling into sports (R3).
    function renderStartMethod() {
      var grid = $('[data-grid="start"]');
      clear(grid);

      grid.appendChild(createWideCard(
        ICONS.browse || '',
        copy.step2_browse_title || 'I want to browse base styles',
        copy.step2_browse_desc || 'Explore our range of base pattern styles',
        state.selectedStartMethod === 'browse',
        function () { selectStartMethod('browse'); }
      ));
      // Only offer the sportswear branch for categories that actually have sport
      // rows. Babies has none, so it shows the browse card only (client feedback).
      var startSports = lookups.categorySports[normKey(state.selectedCategory)] || [];
      if (startSports.length) {
        grid.appendChild(createWideCard(
          ICONS.sport || '',
          copy.step2_sport_title || 'Looking for Sportswear garments.',
          copy.step2_sport_desc || 'Find sport-specific base styles by sport',
          state.selectedStartMethod === 'sport',
          function () { enterSportswear(); }
        ));
      }
    }

    function selectStartMethod(method) {
      state.selectedStartMethod = method;
      state.selectedSport = null;
      state.selectedProductType = null;
      state.selectedStep4 = null;
      state.selectedStep5 = null;
      state.fitFinderRecommended = false;

      hideSteps(['fitfinder', 'sport', 'product', 'step4', 'step5', 'result']);
      renderStartMethod();
      renderProductTypes();
      showStep('product');
      updateBreadcrumb();
    }

    // ========== Fit Finder panel ==========
    // Dormant: nothing reaches this since the "compare a garment" branch was
    // removed. Left in place because its copy settings are still configurable.
    function renderFitFinder() {
      var panel = $('[data-pf="fitfinder-panel"]');
      var iconHtml = panelImages.fitfinder
        ? '<img class="pf-panel__image" src="' + panelImages.fitfinder + '" alt="">'
        : (ICONS.search || '');

      panel.innerHTML = '' +
        iconHtml +
        '<h3 class="pf-panel__title">' + escapeHtml(copy.fitfinder_title || 'We recommend starting with Fit Finder') + '</h3>' +
        '<p class="pf-panel__text">' + escapeHtml(copy.fitfinder_text || '') + '</p>' +
        '<div class="pf-panel__actions">' +
          '<a href="' + (copy.fitfinder_url || '#') + '" class="pf-btn pf-btn--primary" data-pf-action="fitfinder">' +
            (ICONS.external || '') +
            escapeHtml(copy.fitfinder_btn_primary || 'Start with Fit Finder') +
          '</a>' +
          '<button type="button" class="pf-btn pf-btn--ghost" data-pf-action="browse-anyway">' +
            escapeHtml(copy.fitfinder_btn_secondary || 'Browse base styles anyway') +
          '</button>' +
        '</div>';

      var browseBtn = panel.querySelector('[data-pf-action="browse-anyway"]');
      if (browseBtn) {
        browseBtn.addEventListener('click', function () {
          hideStep('fitfinder');
          renderProductTypes();
          showStep('product');
        });
      }
    }

    // ========== Step 3: Product type ==========
    function renderProductTypes() {
      var grid = $('[data-grid="product"]');
      clear(grid);
      var cat = state.selectedCategory;
      var nCat = normKey(cat);
      var types;
      if (state.selectedSport) {
        types = (lookups.sportProductTypes[nCat] && lookups.sportProductTypes[nCat][normKey(state.selectedSport)]) || [];
      } else {
        types = lookups.categoryProductTypes[nCat] || [];
      }
      types.forEach(function (pt) {
        var meta = productTypeMeta[pt] || {};
        grid.appendChild(createCard(
          productTypeLabel(pt),
          resolveProductTypeVisual(pt, cat, meta),
          state.selectedProductType === pt,
          function () { selectProductType(pt); }
        ));
      });
      renderSportEntry();
    }

    // The "Looking for sportswear garments?" entry (Requirement 2), shown on
    // the garment screen whenever the category has sportswear rows. In sport
    // mode it flips to a "back to all garments" link.
    function renderSportEntry() {
      var host = $('[data-pf="sport-entry"]');
      if (!host) return;
      clear(host);
      var sports = lookups.categorySports[normKey(state.selectedCategory)] || [];
      if (!sports.length) return;

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pf-btn pf-btn--ghost pf-sport-entry__btn';
      if (state.selectedSport) {
        btn.textContent = copy.sport_back_label || '← All garments';
        btn.addEventListener('click', exitSportswear);
      } else {
        btn.innerHTML = (ICONS.sport || '') + escapeHtml(copy.sport_entry_label || 'Looking for sportswear garments?');
        btn.addEventListener('click', enterSportswear);
      }
      host.appendChild(btn);
    }

    function selectProductType(pt) {
      state.selectedProductType = pt;
      state.selectedStep4 = null;
      state.selectedStep5 = null;
      hideSteps(['step4', 'step5', 'result']);

      if (!pt) { renderProductTypes(); updateBreadcrumb(); return; }

      renderProductTypes();
      var s4 = getStep4OptionsForPath(state.selectedCategory, state.selectedSport, pt, mapping);

      if (!s4) {
        showResult(state.selectedCategory, state.selectedSport, pt, null, null);
      } else if (s4.length === 1) {
        state.selectedStep4 = s4[0];
        advanceFromStep4();
      } else {
        renderStep4(s4);
        showStep('step4');
      }
      updateBreadcrumb();
    }

    // ========== Sportswear (Requirement 3) ==========
    // Entered from Step 2, straight into the sport list: the category chosen at
    // Step 1 carries through, so there is no gender re-ask (client feedback —
    // it was redundant). Babies never gets here: renderStartMethod() only draws
    // the sportswear card for categories that actually have sports.
    // Flow: sport → garment → step4 → step5 → result.
    function enterSportswear() {
      state.selectedStartMethod = 'sport';
      state.selectedSport = null;
      state.selectedProductType = null;
      state.selectedStep4 = null;
      state.selectedStep5 = null;
      state.fitFinderRecommended = false;
      hideSteps(['fitfinder', 'sport', 'product', 'step4', 'step5', 'result']);
      renderStartMethod();
      renderSports();
      showStep('sport');
      updateBreadcrumb();
    }

    // Leave sportswear and go back to the category's normal garments.
    function exitSportswear() {
      state.selectedSport = null;
      state.selectedProductType = null;
      state.selectedStep4 = null;
      state.selectedStep5 = null;
      hideSteps(['sport', 'step4', 'step5', 'result']);
      renderProductTypes();
      showStep('product');
      updateBreadcrumb();
    }

    // Re-open the sport list (from the breadcrumb) keeping the category.
    function reopenSports() {
      state.selectedProductType = null;
      state.selectedStep4 = null;
      state.selectedStep5 = null;
      hideSteps(['product', 'step4', 'step5', 'result']);
      renderSports();
      showStep('sport');
      updateBreadcrumb();
    }

    function renderSports() {
      var grid = $('[data-grid="sport"]');
      clear(grid);

      var titleEl = $('[data-pf="sport-title"]');
      var descEl = $('[data-pf="sport-desc"]');
      if (titleEl) titleEl.textContent = copy.sport_title || 'Choose your sport';
      if (descEl) descEl.textContent = copy.sport_desc || 'Select a sport to see its garments';

      var sports = lookups.categorySports[normKey(state.selectedCategory)] || [];
      sports.forEach(function (sp) {
        var meta = sportMeta[sp] || {};
        var iconKey = (ICON_MAPS.sport && ICON_MAPS.sport[sp]) || 'sport';
        grid.appendChild(createCard(
          sportLabel(sp),
          resolveSportVisual(sp, state.selectedCategory, meta, iconKey),
          state.selectedSport === sp,
          function () { selectSport(sp); }
        ));
      });
    }

    function selectSport(sp) {
      state.selectedSport = sp;
      state.selectedProductType = null;
      state.selectedStep4 = null;
      state.selectedStep5 = null;
      hideSteps(['step4', 'step5', 'result']);
      renderSports();
      renderProductTypes();
      showStep('product');
      updateBreadcrumb();
    }

    // ========== Step 4 ==========
    // Step 4's heading is both garment-aware (sleeve vs closure) and
    // category-aware, so Men's/Women's/Kids/Babies can each read differently
    // (client feedback). Precedence: per-category setting → general setting →
    // built-in default, so leaving the new fields blank changes nothing.
    function step4Copy(variant, field, fallback) {
      var base = 'step4_' + variant + '_' + field;
      var cat = state.selectedCategory ? String(state.selectedCategory).toLowerCase() : '';
      return (cat && copy[base + '_' + cat]) || copy[base] || fallback;
    }

    function renderStep4(options) {
      var grid = $('[data-grid="step4"]');
      clear(grid);

      var title = $('[data-pf="step4-title"]');
      var desc = $('[data-pf="step4-desc"]');
      if (['Shorts', 'Pants'].indexOf(state.selectedProductType) !== -1) {
        title.textContent = step4Copy('closure', 'title', 'Choose your closure style');
        desc.textContent = step4Copy('closure', 'desc', 'How is the waistband constructed?');
      } else {
        title.textContent = step4Copy('sleeve', 'title', 'Choose your sleeve style');
        desc.textContent = step4Copy('sleeve', 'desc', 'Select the armhole construction type');
      }

      options.forEach(function (opt) {
        var meta = step4Meta[opt] || {};
        grid.appendChild(createCard(
          step4Label(opt, state.selectedCategory),
          resolveStep4Visual(opt, state.selectedCategory, state.selectedProductType, meta),
          state.selectedStep4 === opt,
          function () { selectStep4(opt); }
        ));
      });

      renderCallout('[data-pf="step4-callout"]');
    }

    function selectStep4(opt) {
      state.selectedStep4 = opt;
      state.selectedStep5 = null;
      hideSteps(['step5', 'result']);

      if (!opt) {
        var s4 = getStep4OptionsForPath(state.selectedCategory, state.selectedSport, state.selectedProductType, mapping);
        if (s4) renderStep4(s4);
        updateBreadcrumb();
        return;
      }

      renderStep4(getStep4OptionsForPath(state.selectedCategory, state.selectedSport, state.selectedProductType, mapping));
      advanceFromStep4();
      updateBreadcrumb();
    }

    function advanceFromStep4() {
      var s5 = getStep5OptionsForPath(state.selectedCategory, state.selectedSport, state.selectedProductType, state.selectedStep4, mapping);
      if (!s5) {
        showResult(state.selectedCategory, state.selectedSport, state.selectedProductType, state.selectedStep4, null);
      } else if (s5.length === 1) {
        state.selectedStep5 = s5[0];
        showResult(state.selectedCategory, state.selectedSport, state.selectedProductType, state.selectedStep4, state.selectedStep5);
      } else {
        renderStep5(s5);
        showStep('step5');
      }
    }

    // ========== Step 5 ==========
    function renderStep5(options) {
      var grid = $('[data-grid="step5"]');
      clear(grid);
      options.forEach(function (opt) {
        grid.appendChild(createPill(
          step5Label(opt),
          state.selectedStep5 === opt,
          function () { selectStep5(opt); }
        ));
      });
      // The Fit Finder call-out lives beside Step 4 only; the duplicate that used
      // to sit beside Step 5 was removed (client feedback).
    }

    function selectStep5(opt) {
      state.selectedStep5 = opt;
      hideStep('result');
      renderStep5(getStep5OptionsForPath(state.selectedCategory, state.selectedSport, state.selectedProductType, state.selectedStep4, mapping));
      showResult(state.selectedCategory, state.selectedSport, state.selectedProductType, state.selectedStep4, opt);
      updateBreadcrumb();
    }

    // ========== Result ==========
    function showResult(category, sport, productType, step4, step5) {
      var container = steps.result;
      clear(container);

      var products = resolveProducts(category, sport, productType, step4, step5, mapping);

      // Show the result panel only when at least one matched style has a real
      // builder URL. If nothing is buildable (no matches, or matches that are all
      // URL-less, e.g. Sleeveless Woven Shirt), fall through to the no-match panel
      // instead of the "You are ready…" panel (client feedback).
      // NOTE: this must use resolveRowUrl, not the raw productUrl — a row whose
      // only URL comes from product_urls_json would otherwise be judged
      // unbuildable and the whole panel would drop to the no-match state.
      var anyBuildable = products.some(function (p) { return resolveRowUrl(p) !== ''; });

      if (anyBuildable) {
        var panel = document.createElement('div');
        panel.className = 'pf-panel pf-panel--result';

        var iconHtml = panelImages.result
          ? '<img class="pf-panel__image" src="' + panelImages.result + '" alt="">'
          : (ICONS.check || '');

        panel.innerHTML = iconHtml +
          '<h3 class="pf-panel__title">' + escapeHtml(copy.result_title || 'You are ready for the next step!') + '</h3>' +
          '<p class="pf-panel__text">' + escapeHtml(copy.result_text || '') + '</p>';

        var list = document.createElement('div');
        list.className = 'pf-result-list';

        products.forEach(function (p) {
          var item = document.createElement('div');
          item.className = 'pf-result-item';

          var info = document.createElement('div');
          var nameEl = document.createElement('span');
          nameEl.className = 'pf-result-item__name';
          nameEl.textContent = p.productName;
          info.appendChild(nameEl);

          // Fit-Finder-aware CTA: a style with a real builder URL links to the
          // builder; a style that isn't a product yet (blank/'#' URL) offers the
          // Fit Finder instead, so that path is never lost (Requirement 7).
          var builderUrl = resolveRowUrl(p);
          var link = document.createElement('a');
          link.className = 'pf-btn pf-btn--primary pf-btn--sm';
          if (builderUrl) {
            link.href = builderUrl;
            link.innerHTML = escapeHtml(copy.result_cta_label || 'Continue to Builder') + ' ' + (ICONS.arrow || '');
          } else {
            var ff = copy.fitfinder_url;
            var ffHref = (ff && ff !== '#' && String(ff).trim() !== '') ? ff : '#';
            link.href = ffHref;
            if (ffHref !== '#') { link.target = '_blank'; link.rel = 'noopener'; }
            link.innerHTML = (ICONS.external || '') + escapeHtml(copy.callout_btn || 'Use Fit Finder');
          }

          item.appendChild(info);
          item.appendChild(link);
          list.appendChild(item);
        });

        panel.appendChild(list);

        // The Fit Finder call-out already sits beside Step 4 / Step 5 (the "one
        // above"); a second one here duplicated it on screen, so the result panel
        // is shown on its own (Requirement 6). The per-item CTA above still routes
        // URL-less styles to the Fit Finder.
        container.appendChild(panel);
      } else {
        var noMatch = document.createElement('div');
        noMatch.className = 'pf-panel pf-panel--nomatch';
        noMatch.innerHTML = (ICONS.warning || '') +
          '<h3 class="pf-panel__title">' + escapeHtml(copy.nomatch_title || "We don't currently have a ready-made pattern for this option") + '</h3>' +
          '<p class="pf-panel__text">' + escapeHtml(copy.nomatch_text || "Use Fit Finder and we'll help you find the closest starting point.") + '</p>' +
          '<div class="pf-panel__actions">' +
            '<a href="' + (copy.fitfinder_url || '#') + '" class="pf-btn pf-btn--primary">' + escapeHtml(copy.nomatch_btn_primary || 'Go to Fit Finder') + '</a>' +
            '<button type="button" class="pf-btn pf-btn--secondary" data-pf-action="back-product">' + escapeHtml(copy.nomatch_btn_secondary || 'Choose another product type') + '</button>' +
          '</div>';
        container.appendChild(noMatch);

        var backBtn = noMatch.querySelector('[data-pf-action="back-product"]');
        if (backBtn) {
          backBtn.addEventListener('click', function () {
            selectProductType(null);
            showStep('product');
          });
        }
      }

      showStep('result');
    }

    // Init
    renderCategories();
    updateBreadcrumb();
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ========== Bootstrap all section instances ==========
  function init() {
    var configNodes = document.querySelectorAll('script[data-pf-config]');
    configNodes.forEach(function (node) {
      var root = node.closest('.pf-section');
      if (!root || root.dataset.pfInitialized === '1') return;
      var config;
      try { config = JSON.parse(node.textContent); }
      catch (e) {
        console.error('[PatternFlow] Could not parse config JSON:', e);
        return;
      }
      root.dataset.pfInitialized = '1';
      PatternFlow(root, config);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify section reload (theme editor)
  if (typeof document !== 'undefined') {
    document.addEventListener('shopify:section:load', init);
  }
})();
